import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addDays } from 'date-fns'
import { CalendarPlus, Plus, Zap } from 'lucide-react'
import { CycleCard } from './CycleCard'
import { CreateCycleDialog } from './CreateCycleDialog'
import { GenerateCyclesDialog } from './GenerateCyclesDialog'
import { computeCycleProgress } from './cycleMetrics'
import type { CycleDocument } from '@/lib/db/rxdb/schemas/cycle'
import { useProjectCycles } from '@/lib/db/rxdb/hooks/entities/cycles'
import { useCycleWorkItemsByCycle } from '@/lib/db/rxdb/hooks/entities/cycleWorkItems'
import { useTasks } from '@/lib/db/rxdb/hooks'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { isClosedProgressType } from '@/routes/features/tasks/constants'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface CyclesTabProps {
  projectId: string
  onCycleClick?: (cycleId: string) => void
}

const SCHEDULE_AHEAD = 3

/**
 * Cycles index (design 8a): a timeline strip showing where you are in time,
 * the current cycle leading with its progress, then upcoming and completed.
 */
export function CyclesTab({ projectId, onCycleClick }: CyclesTabProps) {
  const { t } = useTranslation('projects')
  const { cycles, isLoading } = useProjectCycles(projectId)
  const [showCreate, setShowCreate] = useState(false)
  const [showGenerate, setShowGenerate] = useState(false)

  const grouped = useMemo(() => {
    const sorted = [...(cycles ?? [])].sort((a, b) => a.number - b.number)
    return {
      sorted,
      active: sorted.filter((c) => c.status === 'active'),
      draft: sorted.filter((c) => c.status === 'draft'),
      completed: sorted.filter((c) => c.status === 'completed').reverse(),
    }
  }, [cycles])

  // Schedule the next cycles from the day after the last one ends.
  const nextStart = useMemo(() => {
    const last = grouped.sorted.at(-1)
    if (!last) return ''
    // Value for a date <input>, not display text.
    const next = addDays(last.endDate, 1)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`
  }, [grouped.sorted])

  return (
    <div className="h-full overflow-y-auto px-4 pb-10 pt-8 md:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t('cycles.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('cycles.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowGenerate(true)}>
            <CalendarPlus className="mr-1.5 size-3.5" />
            {t('cycles.scheduleNext', { count: SCHEDULE_AHEAD })}
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1.5 size-3.5" />
            {t('cycles.newCycle')}
          </Button>
        </div>
      </div>

      {!isLoading && grouped.sorted.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Zap className="mb-3 size-10 text-muted-foreground/50" />
          <h3 className="mb-1 text-sm font-medium">No cycles yet</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            Create your first cycle to start planning sprints
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowGenerate(true)}>
              {t('cycles.scheduleNext', { count: SCHEDULE_AHEAD })}
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              {t('cycles.newCycle')}
            </Button>
          </div>
        </div>
      )}

      {grouped.sorted.length > 0 && (
        <CycleTimeline cycles={grouped.sorted} onCycleClick={onCycleClick} />
      )}

      {grouped.active.length > 0 && (
        <section className="mb-7">
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {t('cycles.current')}
          </h3>
          <div className="space-y-2">
            {grouped.active.map((cycle) => (
              <CurrentCycleCard key={cycle.id} cycle={cycle} onClick={() => onCycleClick?.(cycle.id)} />
            ))}
          </div>
        </section>
      )}

      {grouped.draft.length > 0 && (
        <section className="mb-7">
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {t('cycles.upcoming')}
          </h3>
          <div className="space-y-2">
            {grouped.draft.map((cycle) => (
              <CycleCard key={cycle.id} cycle={cycle} projectId={projectId} onClick={() => onCycleClick?.(cycle.id)} />
            ))}
          </div>
        </section>
      )}

      {grouped.completed.length > 0 && (
        <section>
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {t('cycles.completedCount', { count: grouped.completed.length })}
          </h3>
          <div className="space-y-2">
            {grouped.completed.map((cycle) => (
              <CycleCard key={cycle.id} cycle={cycle} projectId={projectId} onClick={() => onCycleClick?.(cycle.id)} />
            ))}
          </div>
        </section>
      )}

      <CreateCycleDialog projectId={projectId} open={showCreate} onOpenChange={setShowCreate} />
      <GenerateCyclesDialog
        projectId={projectId}
        open={showGenerate}
        onOpenChange={setShowGenerate}
        defaultCount={SCHEDULE_AHEAD}
        defaultStartDate={nextStart}
      />
    </div>
  )
}

/**
 * Where you are in time: recent and upcoming cycles laid out on one track with
 * a Today marker (design 8a).
 */
function CycleTimeline({
  cycles,
  onCycleClick,
}: {
  cycles: Array<CycleDocument>
  onCycleClick?: (cycleId: string) => void
}) {
  const { t } = useTranslation('projects')
  const { date: formatDate } = useDateTimeFormatters()
  const now = Date.now()

  // Two cycles of history, the current one, and up to three ahead.
  const visible = useMemo(() => {
    const activeIndex = cycles.findIndex((c) => c.status === 'active')
    const pivot = activeIndex >= 0 ? activeIndex : cycles.findIndex((c) => c.status === 'draft')
    const start = Math.max(0, (pivot >= 0 ? pivot : cycles.length) - 2)
    return cycles.slice(start, start + 6)
  }, [cycles])

  const first = visible[0]
  const last = visible.at(-1)
  if (!first || !last) return null
  const from = first.startDate
  const to = last.endDate
  const span = Math.max(1, to - from)
  const pct = (ms: number) => `${Math.min(100, Math.max(0, ((ms - from) / span) * 100))}%`
  const showToday = now >= from && now <= to
  // Pin the label to the marker's side near either end so it stays inside the card.
  const todayRatio = (now - from) / span
  const todayLabelAlign = todayRatio < 0.08 ? 'left-0' : todayRatio > 0.92 ? 'right-0' : 'left-1/2 -translate-x-1/2'

  return (
    <div className={cn('mb-8 rounded-xl border bg-card px-4 pb-3', showToday ? 'pt-7' : 'pt-4')}>
      <div className="relative h-9">
        {visible.map((cycle) => (
          <button
            key={cycle.id}
            type="button"
            onClick={() => onCycleClick?.(cycle.id)}
            title={`${cycle.name} · ${formatDate(new Date(cycle.startDate), { month: 'short', day: 'numeric' })} – ${formatDate(new Date(cycle.endDate), { month: 'short', day: 'numeric' })}`}
            className={cn(
              'absolute inset-y-0 flex items-center overflow-hidden rounded-md border px-2 text-left text-[11.5px] font-medium transition-colors',
              cycle.status === 'active' && 'border-brand bg-brand-tint text-brand-tint-foreground',
              cycle.status === 'completed' && 'border-transparent bg-secondary text-muted-foreground',
              cycle.status === 'draft' && 'border-dashed bg-card text-secondary-foreground hover:bg-secondary/50',
            )}
            style={{ left: pct(cycle.startDate), width: `calc(${pct(cycle.endDate)} - ${pct(cycle.startDate)} - 3px)` }}
          >
            <span className="truncate">{cycle.name}</span>
          </button>
        ))}
        {showToday && (
          <div className="pointer-events-none absolute -bottom-1 -top-1 w-px bg-foreground" style={{ left: pct(now) }}>
            <span
              className={cn(
                'absolute bottom-full mb-0.5 whitespace-nowrap text-[10px] font-semibold uppercase leading-none tracking-wide',
                todayLabelAlign,
              )}
            >
              {t('cycles.today')}
            </span>
          </div>
        )}
      </div>
      <div className="mt-2 flex justify-between text-[10.5px] text-muted-foreground">
        <span>{formatDate(new Date(from), { month: 'short', day: 'numeric' })}</span>
        <span>{formatDate(new Date(to), { month: 'short', day: 'numeric' })}</span>
      </div>
    </div>
  )
}

/** The active cycle, leading the page with live progress. */
function CurrentCycleCard({ cycle, onClick }: { cycle: CycleDocument; onClick: () => void }) {
  const { t } = useTranslation('projects')
  const { date: formatDate } = useDateTimeFormatters()
  const { cycleWorkItems } = useCycleWorkItemsByCycle(cycle.id)
  const { tasks } = useTasks()
  const { all } = useStatuses()

  const progress = useMemo(() => {
    const closed = new Set(all.statuses.filter((s) => isClosedProgressType(s.progressType)).map((s) => s.id))
    return computeCycleProgress({ cycle, workItems: cycleWorkItems ?? [], tasks, closedStatusIds: closed })
  }, [cycle, cycleWorkItems, tasks, all.statuses])

  const pct = progress.scope > 0 ? Math.round((progress.done / progress.scope) * 100) : 0

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-xl border bg-card p-4 text-left transition-colors hover:border-input"
    >
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="text-[15px] font-semibold">{cycle.name}</span>
        <span className="rounded-md bg-brand-tint px-2 py-0.5 text-[11px] font-medium text-brand-tint-foreground">
          {t('cycles.status.active')} · {t('cycles.dayOf', { day: progress.workingDay, total: progress.totalWorkingDays })}
        </span>
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {t('cycles.doneOfScope', { done: progress.done, scope: progress.scope })}
        </span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        {formatDate(new Date(cycle.startDate), { month: 'short', day: 'numeric' })} –{' '}
        {formatDate(new Date(cycle.endDate), { month: 'short', day: 'numeric' })} ·{' '}
        {t('cycles.daysLeft', { count: progress.workingDaysLeft })}
        {cycle.description ? ` · ${cycle.description}` : ''}
      </p>
      <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-brand transition-[width]" style={{ width: `${pct}%` }} />
      </div>
    </button>
  )
}
